import type { DirectorConfig } from '@/types/director'
import { CheckCircle2, XCircle, Undo2, MessageCircleQuestion, MessageSquare } from 'lucide-react'

interface CaseActionFooterProps {
  config: DirectorConfig | null
  readOnly: boolean
  reviseDecision?: boolean
  booked?: boolean
  abortSearch?: boolean
  onApprove: () => void
  onReject: () => void
  onAbortSearch?: () => void
  onInfo: () => void
  onReturn: () => void
  onChat: () => void
}

/** Fallakte v2 (P5.8/K2/K8): fixe Aktionsleiste, rechtegesteuert. */
export function CaseActionFooter({
  config, readOnly, reviseDecision, booked, abortSearch, onApprove, onReject, onAbortSearch, onInfo, onReturn, onChat,
}: CaseActionFooterProps) {
  if (readOnly && !reviseDecision) return null

  const canApprove = config?.can_approve !== false && !booked
  const canReject = config?.can_reject !== false && !booked
  const canInfo = !readOnly && config?.can_info_request !== false
  // Kap. 8: 'Anpassen/zurückstellen' braucht can_adjust_case (Default False)
  const canReturn = !readOnly && config?.can_adjust_case === true

  const hint = readOnly
    ? (booked
      ? 'Gebucht — Genehmigt/Abgelehnt gesperrt, Zahler änderbar'
      : 'Entscheidung nachträglich ändern · wird protokolliert')
    : 'Entscheid wird protokolliert · Zahlung & Zeitausweis im Dialog'

  return (
    <div className="h-14 bg-white border-t border-gray-200 flex items-center gap-3 px-4 sm:px-5 shrink-0 overflow-hidden">
      <span className="text-xs text-gray-400 hidden 2xl:block truncate min-w-0" title={hint}>
        {hint}
      </span>
      <div className="flex items-center gap-1.5 sm:gap-2 flex-nowrap ml-auto min-w-0">
        <button
          type="button"
          onClick={onChat}
          className="shrink-0 px-2.5 py-1.5 border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-50 text-xs font-medium whitespace-nowrap"
          title="Im Chat mit der Lehrperson einigen"
        >
          <span className="flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5 shrink-0" /> Chat
          </span>
        </button>
        {!readOnly && (
          <button
            type="button"
            onClick={onInfo}
            disabled={!canInfo}
            title={canInfo ? undefined : 'Erfordert eine Rechte-Freigabe durch den Superadmin.'}
            className="shrink-0 px-2.5 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-xs font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="flex items-center gap-1.5">
              <MessageCircleQuestion className="w-3.5 h-3.5 shrink-0" /> Rückfrage
            </span>
          </button>
        )}
        {!readOnly && !abortSearch && (
          <button
            type="button"
            onClick={onReturn}
            disabled={!canReturn}
            title={canReturn ? undefined : 'Erfordert eine Rechte-Freigabe durch den Superadmin.'}
            className="shrink-0 px-2.5 py-1.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-xs font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="flex items-center gap-1.5">
              <Undo2 className="w-3.5 h-3.5 shrink-0" /> Anpassen
            </span>
          </button>
        )}
        {abortSearch ? (
          <button
            type="button"
            onClick={onAbortSearch}
            disabled={booked}
            title={booked ? 'Gebuchte Anfrage kann nicht mehr abgebrochen werden.' : 'Suche abbrechen'}
            className="shrink-0 px-2.5 py-1.5 border border-red-200 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 text-xs font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="flex items-center gap-1.5">
              <XCircle className="w-3.5 h-3.5 shrink-0" /> Suche abbrechen
            </span>
          </button>
        ) : (
          <button
            type="button"
            onClick={onReject}
            disabled={!canReject}
            title={booked
              ? 'Gebuchte Anfrage kann nicht mehr abgelehnt werden.'
              : canReject ? undefined : 'Erfordert eine Rechte-Freigabe durch den Superadmin.'}
            className="shrink-0 px-2.5 py-1.5 border border-red-200 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 text-xs font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="flex items-center gap-1.5">
              <XCircle className="w-3.5 h-3.5 shrink-0" /> Ablehnen
            </span>
          </button>
        )}
        <button
          type="button"
          onClick={onApprove}
          disabled={!canApprove}
          title={booked
            ? 'Gebuchte Anfrage kann nicht mehr neu entschieden werden.'
            : canApprove ? undefined : 'Erfordert eine Rechte-Freigabe durch den Superadmin.'}
          className="shrink-0 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 shadow-sm text-xs font-semibold whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> {abortSearch ? 'Zahler' : 'Genehmigen'}
          </span>
        </button>
      </div>
    </div>
  )
}
